/**
 * User Dashboard
 *
 * Displays personalized greeting and viewing statistics for authenticated users.
 * Shows titles watched, watch time, continue watching count, likes, and watchlist.
 */

import { Film, Settings, User, Users, type LucideIcon } from "lucide-react";
import { useEffect, useState, type ReactElement } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../auth/AuthContext";
import { apiCall } from "../../config/api";
import { StatCard } from "../../shared/components/StatCard";
import { Card } from "../../shared/components/ui/card";

interface UserStats {
  titlesWatched: number;
  totalWatchTimeHours: number;
  continueWatching: number;
  likedTitles: number;
  watchlistItems: number;
}

interface QuickLinkCardProps {
  readonly to: string;
  readonly icon: LucideIcon;
  readonly label: string;
  readonly description: string;
}

function QuickLinkCard({ to, icon: Icon, label, description }: QuickLinkCardProps) {
  return (
    <Link to={to}>
      <Card className="bg-transparent border-neutral-800 rounded-lg p-6 hover:border-lumi-accent/50 transition-all group">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-neutral-800/50 rounded-lg group-hover:bg-lumi-accent/10 transition-colors">
            <Icon size={18} className="text-neutral-400 group-hover:text-lumi-accent transition-colors" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-medium text-white group-hover:text-lumi-accent transition-colors">{label}</p>
            <p className="text-xs text-neutral-500 group-hover:text-white transition-colors">{description}</p>
          </div>
        </div>
      </Card>
    </Link>
  );
}

function getGreeting(): string {
  const hour = new Date().getHours();
  if (hour < 12) return "Good morning";
  if (hour < 18) return "Good afternoon";
  return "Good evening";
}

// Stable keys for skeleton cards
const SKELETON_ITEMS = ["stat-1", "stat-2", "stat-3", "stat-4", "stat-5"] as const;

/**
 * Loading skeleton for stat cards
 */
function LoadingSkeleton() {
  return (
    <>
      {SKELETON_ITEMS.map((id) => (
        <Card key={id} className="bg-neutral-900/40 border-neutral-800 p-4 animate-pulse">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-neutral-800/50 w-[34px] h-[34px]" />
            <div className="flex-1 space-y-1.5">
              <div className="h-3 bg-neutral-800 rounded w-20" />
              <div className="h-5 bg-neutral-800 rounded w-12" />
            </div>
          </div>
        </Card>
      ))}
    </>
  );
}

/**
 * Welcome message for new users
 */
function WelcomeMessage() {
  return (
    <Card className="bg-neutral-900/40 border-neutral-800 p-8 text-center">
      <div className="max-w-md mx-auto space-y-4">
        <div className="p-4 bg-lumi-accent/10 rounded-full w-20 h-20 mx-auto flex items-center justify-center">
          <Film size={40} className="text-lumi-accent" />
        </div>
        <h2 className="text-xl font-semibold text-white">Welcome to Lumi</h2>
        <p className="text-neutral-400">
          Start exploring our collection to see your viewing stats here. Your watch progress, favorites, and
          recommendations will appear as you engage with content.
        </p>
      </div>
    </Card>
  );
}

/**
 * User Dashboard component with viewing stats
 */

export function Dashboard(): ReactElement {
  const { user } = useAuth();
  const [stats, setStats] = useState<UserStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        const response = await apiCall("/v1/user/stats");

        if (!response.ok) {
          throw new Error("Failed to fetch stats");
        }

        const data = (await response.json()) as { stats: UserStats };
        setStats(data.stats);
      } catch (err) {
        console.error("Failed to fetch user stats:", err);
        setError(err instanceof Error ? err.message : "Failed to load stats");
      } finally {
        setLoading(false);
      }
    };

    void fetchStats();
  }, []);

  const displayName = user?.displayName || user?.email?.split("@")[0] || "there";

  return (
    <div className="space-y-8">
      {/* Greeting Header */}
      <div className="space-y-2">
        <h1 className="text-2xl font-bold text-white mb-2">
          {getGreeting()}, {displayName}
        </h1>
        <p className="text-xs text-neutral-400 pb-6">Here's an overview of your viewing activity</p>
      </div>

      {/* Error State */}
      {error && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-6 text-center">
          <p className="text-red-400">{error}</p>
        </div>
      )}

      {/* Quick Links */}
      <div>
        <h2 className="text-lg font-semibold text-white mb-4">Quick Settings</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <QuickLinkCard to="/user/account" icon={User} label="Account" description="Manage your profile" />
          <QuickLinkCard to="/user/profiles" icon={Users} label="Profiles" description="Switch or manage profiles" />
          <QuickLinkCard to="/user/settings" icon={Settings} label="App Settings" description="Preferences & options" />
        </div>
      </div>

      {/* Stats Grid */}
      <div>
        <h2 className="text-lg font-semibold text-white mb-4">Your Activity</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {loading ? (
            <LoadingSkeleton />
          ) : (
            <>
              <StatCard label="Titles Watched" value={stats?.titlesWatched ?? 0} color="text-lumi-accent" />
              <StatCard label="Hours Watched" value={`${stats?.totalWatchTimeHours ?? 0}h`} />
              <StatCard label="Continue Watching" value={stats?.continueWatching ?? 0} />
              <StatCard label="Liked Titles" value={stats?.likedTitles ?? 0} />
              <StatCard label="Watchlist Items" value={stats?.watchlistItems ?? 0} />
            </>
          )}
        </div>
      </div>

      {/* Welcome Message */}
      {!loading && stats?.titlesWatched === 0 && <WelcomeMessage />}
    </div>
  );
}
